import { BaseElement } from '../../../base-element';
import type { GridMetadata, GridSelectionItem, TrialEventSummaryDto } from '../../../../api/types';
import '../../../ui/dt-section-card';
import { getSectionCardState } from './section-card-state';

export interface ClassSelectionsChangeDetail {
  selections: GridSelectionItem[];
}

export class ClassSelectionsGrid extends BaseElement {
  private _trial: TrialEventSummaryDto | null = null;
  private _gridMetadata: GridMetadata | null = null;
  private _selections: GridSelectionItem[] = [];

  private rows: string[] = ['Sheep', 'Cattle', 'Ducks', 'Mixed'];
  private classCols: string[] = [
    'STD',
    'OPN',
    'ADV',
    'FTD_OPN',
    'FTD_ADV',
    'NOV',
    'WRK_JR_HNDLR',
    'FEO',
    'POST_ADV',
    'RTD',
  ];
  private dateCols: string[] = ['DATE1_TRIAL1', 'DATE1_TRIAL2'];
  private gridCols: string[] = [...this.classCols, ...this.dateCols];
  private daySlots: number[] = [1];
  private defaultDayLabels: string[] = ['Day 1'];
  private dayDates: Date[] = [];
  private disabledSet = new Set<string>();
  private activeCell: { row: number; col: number } = { row: 0, col: 0 };

  get trial(): TrialEventSummaryDto | null {
    return this._trial;
  }

  set trial(value: TrialEventSummaryDto | null) {
    this._trial = value;
    this.updateRender();
  }

  get gridMetadata(): GridMetadata | null {
    return this._gridMetadata;
  }

  set gridMetadata(value: GridMetadata | null) {
    this._gridMetadata = value;
    this.updateRender();
  }

  get selections(): GridSelectionItem[] {
    return this._selections;
  }

  set selections(value: GridSelectionItem[]) {
    this._selections = value ?? [];
    this.updateRender();
  }

  connectedCallback(): void {
    this.updateRender();
  }

  private refreshGrid(): void {
    // Availability cannot be inferred from a trial summary or template.
    const metadata = this._gridMetadata ?? { rows: [], cols: [], disabledCells: [] };

    this.rows = metadata.rows;
    this.disabledSet = new Set(
      metadata.disabledCells.map((cell) => `${cell.row}:${cell.col}`)
    );
    const allCols = [...metadata.cols];
    this.classCols = allCols.filter((col) => !this.isDateCol(col));
    this.dateCols = allCols.filter((col) => this.isDateCol(col));
    this.gridCols = [...this.classCols, ...this.dateCols];

    this.daySlots = this.buildDaySlots();
    this.defaultDayLabels = this.daySlots.map((slot) => `Day ${slot}`);
    this.dayDates = this.buildDayDates();
    const currentRow = this.rows[this.activeCell.row];
    const currentCol = this.gridCols[this.activeCell.col];
    const activeValid =
      currentRow &&
      currentCol &&
      !this.isCellDisabled(currentRow, currentCol) &&
      this.activeCell.row < this.rows.length &&
      this.activeCell.col < this.gridCols.length;
    if (!activeValid) {
      const firstFocusable = this.findFirstFocusable();
      if (firstFocusable) {
        this.activeCell = firstFocusable;
      }
    }
  }

  private isCellDisabled(row: string, col: string): boolean {
    return this.disabledSet.has(`${row}:${col}`);
  }

  private isSelected(row: string, col: string): boolean {
    return this._selections.some(
      (selection) => selection.row === row && selection.col === col
    );
  }

  private focusCurrentCell(rowIndex: number, colIndex: number): void {
    const scheduleFocus = typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (callback: FrameRequestCallback) => window.setTimeout(callback, 0);

    scheduleFocus(() => this.focusCell(rowIndex, colIndex));
  }

  private toggleCell(row: string, col: string, rowIndex: number, colIndex: number): void {
    if (this.isCellDisabled(row, col)) {
      return;
    }

    this.setActiveCell(rowIndex, colIndex);
    const selections = [...this._selections];
    const isClassColumn = !this.isDateCol(col);

    if (isClassColumn) {
      // Remove any existing class selection for this row
      const existingClassIndex = selections.findIndex(
        (s) => s.row === row && !this.isDateCol(s.col)
      );

      if (existingClassIndex >= 0) {
        const existing = selections[existingClassIndex];
        if (existing.col === col) {
          // Same cell — toggle off (deselect)
          selections.splice(existingClassIndex, 1);
          this._selections = selections;
          this.updateRender();
          this.focusCurrentCell(rowIndex, colIndex);
          this.emit<ClassSelectionsChangeDetail>('selections-change', { selections });
          return;
        }
        // Different class — remove old, add new
        selections.splice(existingClassIndex, 1);
      }

      // Add new class selection
      selections.push({ row, col, value: 'X' });
    } else {
      // Date column: toggle behavior (checkbox)
      const index = selections.findIndex(
        (s) => s.row === row && s.col === col
      );

      if (index >= 0) {
        selections.splice(index, 1);
      } else {
        selections.push({ row, col, value: 'X' });
      }
    }

    this._selections = selections;
    this.updateRender();
    this.focusCurrentCell(rowIndex, colIndex);
    this.emit<ClassSelectionsChangeDetail>('selections-change', { selections });
  }

  private onCellKeydown(event: KeyboardEvent, rowIndex: number, colIndex: number): void {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.moveFocus(rowIndex, colIndex, 0, 1);
        return;
      case 'ArrowLeft':
        event.preventDefault();
        this.moveFocus(rowIndex, colIndex, 0, -1);
        return;
      case 'ArrowDown':
        event.preventDefault();
        this.moveFocus(rowIndex, colIndex, 1, 0);
        return;
      case 'ArrowUp':
        event.preventDefault();
        this.moveFocus(rowIndex, colIndex, -1, 0);
        return;
      case 'Enter': {
        event.preventDefault();
        const row = this.rows[rowIndex];
        const col = this.gridCols[colIndex];
        if (row && col) {
          this.toggleCell(row, col, rowIndex, colIndex);
        }
        return;
      }
      default:
        return;
    }
  }

  private setActiveCell(rowIndex: number, colIndex: number): void {
    this.activeCell = { row: rowIndex, col: colIndex };
    this.updateTabIndexes();
  }

  private getTabIndex(rowIndex: number, colIndex: number, row: string, col: string): number {
    if (this.isCellDisabled(row, col)) {
      return -1;
    }

    return this.activeCell.row === rowIndex && this.activeCell.col === colIndex ? 0 : -1;
  }

  private buildAriaLabel(row: string, col: string): string {
    if (this.isDateCol(col)) {
      const slot = this.parseDateCol(col);
      if (slot) {
        return `${row} day ${slot.day} trial ${slot.trial}`;
      }
    }

    return `${row} ${this.formatColLabel(col)}`;
  }

  private formatColLabel(col: string): string {
    return col.replace(/_/g, ' ');
  }

  private buildDayDates(): Date[] {
    if (!this._trial?.startDate) {
      return [];
    }

    const start = this.parseDateOnly(this._trial.startDate);
    if (!start) {
      return [];
    }

    return this.daySlots.map((slot) =>
      new Date(start.getFullYear(), start.getMonth(), start.getDate() + slot - 1)
    );
  }

  private buildDaySlots(): number[] {
    const slots = this.dateCols
      .map((col) => this.parseDateCol(col)?.day ?? null)
      .filter((day): day is number => day !== null);

    if (slots.length === 0) {
      return [1];
    }

    return Array.from(new Set(slots)).sort((a, b) => a - b);
  }

  private moveFocus(
    rowIndex: number,
    colIndex: number,
    deltaRow: number,
    deltaCol: number
  ): void {
    const rowCount = this.rows.length;
    const colCount = this.gridCols.length;
    let nextRow = rowIndex + deltaRow;
    let nextCol = colIndex + deltaCol;

    while (nextRow >= 0 && nextRow < rowCount && nextCol >= 0 && nextCol < colCount) {
      const row = this.rows[nextRow];
      const col = this.gridCols[nextCol];
      if (row && col && !this.isCellDisabled(row, col)) {
        this.setActiveCell(nextRow, nextCol);
        this.focusCell(nextRow, nextCol);
        return;
      }

      nextRow += deltaRow;
      nextCol += deltaCol;
    }
  }

  private focusCell(rowIndex: number, colIndex: number): void {
    const selector = `input[data-row="${rowIndex}"][data-col="${colIndex}"]`;
    const checkbox = this.$(selector) as HTMLInputElement | null;
    checkbox?.focus();
  }

  private findFirstFocusable(): { row: number; col: number } | null {
    for (let rowIndex = 0; rowIndex < this.rows.length; rowIndex += 1) {
      for (let colIndex = 0; colIndex < this.gridCols.length; colIndex += 1) {
        const row = this.rows[rowIndex];
        const col = this.gridCols[colIndex];
        if (row && col && !this.isCellDisabled(row, col)) {
          return { row: rowIndex, col: colIndex };
        }
      }
    }

    return null;
  }

  private isDateCol(col: string): boolean {
    return col.startsWith('DATE');
  }

  private parseDateCol(col: string): { day: number; trial: number } | null {
    const match = col.match(/^DATE(\d+)_TRIAL(\d+)$/);
    if (!match) {
      return null;
    }

    const day = Number(match[1]);
    const trial = Number(match[2]);
    if (Number.isNaN(day) || Number.isNaN(trial)) {
      return null;
    }

    return { day, trial };
  }

  private parseDateOnly(value: string): Date | null {
    const parts = value.split('-').map((part) => Number(part));
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      return null;
    }

    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  private attachEventListeners(): void {
    this.$$('input[data-row][data-col]').forEach((checkbox) => {
      const rowIndex = Number(checkbox.getAttribute('data-row'));
      const colIndex = Number(checkbox.getAttribute('data-col'));
      checkbox.addEventListener('change', () => {
        const row = this.rows[rowIndex];
        const col = this.gridCols[colIndex];
        if (row && col) {
          this.toggleCell(row, col, rowIndex, colIndex);
        }
      });
      checkbox.addEventListener('focus', () => this.setActiveCell(rowIndex, colIndex));
      checkbox.addEventListener('keydown', (event) => this.onCellKeydown(event, rowIndex, colIndex));
    });
  }

  private updateTabIndexes(): void {
    this.$$('input[data-row][data-col]').forEach((checkbox) => {
      const rowIndex = Number(checkbox.getAttribute('data-row'));
      const colIndex = Number(checkbox.getAttribute('data-col'));
      const row = this.rows[rowIndex];
      const col = this.gridCols[colIndex];
      if (!row || !col) return;
      const tabIndex = this.getTabIndex(rowIndex, colIndex, row, col);
      checkbox.setAttribute('tabindex', tabIndex.toString());
    });
  }

  private updateRender(): void {
    const scrollLeft = this.$('.dt-class-selections-grid-scrollbar')?.scrollLeft ?? 0;
    const restoreFocus = document.activeElement instanceof HTMLInputElement &&
      this.contains(document.activeElement);
    this.refreshGrid();
    const sectionState = getSectionCardState(['selections']);
    const errorCountAttr = sectionState.errorCount > 0 ? `error-count="${sectionState.errorCount}"` : '';
    const completeAttr = sectionState.complete ? 'complete="true"' : '';

    if (!this._gridMetadata) {
      this.render(`
        <dt-section-card heading="Class Selections Grid" icon="grid_view">
          <p role="alert" class="px-4 py-3 text-sm text-neutral-700">
            Class availability is unavailable. Reload the page before selecting classes.
          </p>
        </dt-section-card>
      `);
      return;
    }

    const dayHeaders = this.dayDates.length
      ? this.dayDates
          .map(
            (dayDate) => `
            <th class="bg-primary-50/30 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600" colspan="2">
              ${dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </th>
          `
          )
          .join('')
      : this.defaultDayLabels
          .map(
            (label) => `
            <th class="bg-primary-50/30 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600" colspan="2">
              ${label}
            </th>
          `
          )
          .join('');

    const trialHeaders = this.daySlots
      .map(
        () => `
          <th class="bg-primary-50/30 px-3 py-2 text-xs font-medium text-neutral-600">Trial #1 (AM)</th>
          <th class="bg-primary-50/30 px-3 py-2 text-xs font-medium text-neutral-600">Trial #2 (PM)</th>
        `
      )
      .join('');

    const rowsHtml = this.rows
      .map((row, rowIndex) => {
        const cells = this.gridCols
          .map((col, colIndex) => {
            const isDisabled = this.isCellDisabled(row, col);
            const isSelected = this.isSelected(row, col);
            const isDateCol = this.isDateCol(col);
            const cellBackgroundClass = isDisabled
              ? 'bg-neutral-100 bg-[repeating-linear-gradient(45deg,_transparent,_transparent_4px,_rgba(0,0,0,0.05)_4px,_rgba(0,0,0,0.05)_8px)] text-neutral-400'
              : isSelected
                ? isDateCol
                  ? 'bg-primary-100'
                  : 'bg-primary-100/50'
                : isDateCol
                  ? 'bg-primary-50/30'
                  : 'bg-white';

            return `
              <td class="border border-neutral-200 p-0 ${cellBackgroundClass}" role="gridcell" data-cell-key="${row}:${col}" ${isDisabled ? 'aria-disabled="true"' : ''}>
                <div class="flex h-12 items-center justify-center px-2 py-3">
                  ${isDisabled
                    ? `<span class="sr-only">${this.buildAriaLabel(row, col)} unavailable</span>`
                    : `
                      <input
                        type="checkbox"
                        class="form-checkbox h-5 w-5 rounded border-neutral-300 text-primary-500 focus:ring-primary-500"
                        ${isSelected ? 'checked' : ''}
                        data-row="${rowIndex}"
                        data-col="${colIndex}"
                        tabindex="${this.getTabIndex(rowIndex, colIndex, row, col)}"
                        aria-label="${this.buildAriaLabel(row, col)}"
                      />
                    `}
                </div>
              </td>
            `;
          })
          .join('');

        return `
          <tr role="row">
            <th class="sticky left-0 z-10 border-y border-neutral-200 border-r bg-white px-3 py-2 text-left text-sm font-semibold text-neutral-700" scope="row">
              ${row}
            </th>
            ${cells}
          </tr>
        `;
      })
      .join('');

    this.render(`
      <dt-section-card
        data-testid="class-selections-grid"
        heading="Class Selections Grid"
        icon="grid_view"
        ${errorCountAttr}
        ${completeAttr}
      >
        <div class="dt-class-selections-grid-scrollbar overflow-x-auto">
          <table class="min-w-[1200px] border-collapse text-xs" role="grid">
            <thead>
              <tr>
                <th class="sticky left-0 z-20 border border-neutral-200 bg-neutral-100 px-3 py-2" rowspan="2"></th>
                ${this.classCols
                  .map(
                    (col) => `
                      <th class="border border-neutral-200 bg-neutral-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600" rowspan="2">
                        ${this.formatColLabel(col)}
                      </th>
                    `
                  )
                  .join('')}
                ${dayHeaders}
              </tr>
              <tr>
                ${trialHeaders}
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </div>
        <div class="border-t border-neutral-100 bg-neutral-50 px-4 py-3 text-xs text-neutral-500 sm:px-6">
          <span class="font-medium">Note:</span> Check the classes and trial dates you want to enter. Mixed can only enter FTD OPN and FTD ADV. Ducks cannot enter Post Advanced or RTD.
        </div>
      </dt-section-card>
    `);

    this.attachEventListeners();
    const scroller = this.$('.dt-class-selections-grid-scrollbar');
    if (scroller) scroller.scrollLeft = scrollLeft;
    if (restoreFocus) this.focusCell(this.activeCell.row, this.activeCell.col);
  }
}

customElements.define('dt-class-selections-grid', ClassSelectionsGrid);
